/**
 * Shared Orderbook Card Components
 *
 * Extracted from StandbyHedgedConfirmDialog for reuse across:
 * - StandbyHedgedConfirmDialog
 * - UpdateBudgetDialog
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Stack,
  Paper,
  Typography,
  CircularProgress,
  Avatar,
  Tooltip,
} from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import { getExchangeLogo } from '../../utils/exchangeUrls';
import { formatPrice } from '../../utils/formatters';
import { FundingCountdown } from '../FuturesSpotTable/FundingCountdown';
import type { OrderbookPriceLevel } from '../../types/orderbook';

/**
 * Calculate which levels are covered by budget and return average price
 * Returns { highlightedLevels: number[], avgPrice: number | null, totalVolume: number }
 */
export function calculateBudgetCoverage(
  levels: OrderbookPriceLevel[],
  budgetInQuote: number
): { highlightCount: number; avgPrice: number | null; totalVolume: number } {
  if (levels.length === 0 || budgetInQuote <= 0) {
    return { highlightCount: 0, avgPrice: null, totalVolume: 0 };
  }

  let remainingBudget = budgetInQuote;
  let totalCost = 0;
  let totalVolume = 0;
  let highlightCount = 0;

  for (const level of levels) {
    const levelCost = level.price * level.volume;

    if (remainingBudget <= 0) break;

    if (levelCost <= remainingBudget) {
      // Fully consume this level
      totalCost += levelCost;
      totalVolume += level.volume;
      remainingBudget -= levelCost;
      highlightCount++;
    } else {
      // Partially consume this level
      const partialVolume = remainingBudget / level.price;
      totalCost += remainingBudget;
      totalVolume += partialVolume;
      highlightCount++;
      break;
    }
  }

  const avgPrice = totalVolume > 0 ? totalCost / totalVolume : null;
  return { highlightCount, avgPrice, totalVolume };
}

/**
 * Futures Card Component Props
 */
interface FuturesOrderbookCardProps {
  bids: OrderbookPriceLevel[];
  loading: boolean;
  error: string | null;
  fundingRate: number | null;
  nextFundingTime: number | null;
  avgPrice: number | null;
  highlightCount: number;
  /** Compact mode for smaller dialogs */
  compact?: boolean;
}

/**
 * Spot Card Component Props
 */
interface SpotOrderbookCardProps {
  exchangeName: string;
  exchangeIndex: number;
  asks: OrderbookPriceLevel[];
  loading: boolean;
  error: string | null;
  quoteCurrency: 'K' | 'U' | 'B';
  diffPercent: number | null;
  avgPrice: number | null;
  highlightCount: number;
  /** Rate to convert quote currency to USDT (krwToUsdtRate for KRW, btcUsdtRate for BTC) */
  quoteToUsdtRate: number | null;
  /** Compact mode for smaller dialogs */
  compact?: boolean;
}

/**
 * Futures Card - Shows funding rate + countdown timer
 * Highlights bid levels covered by budget
 */
export const FuturesOrderbookCard: React.FC<FuturesOrderbookCardProps> = ({
  bids,
  loading,
  error,
  fundingRate,
  nextFundingTime,
  avgPrice,
  highlightCount,
  compact = false,
}) => {
  const { t } = useTranslation();
  const hasData = bids.length > 0;

  // Sort bids descending (highest first)
  const sortedBids = [...bids].sort((a, b) => b.price - a.price);

  return (
    <Paper
      elevation={3}
      sx={{
        p: compact ? 2 : 3,
        borderRadius: 2,
        background: 'linear-gradient(135deg, rgba(25,118,210,0.15) 0%, rgba(66,165,245,0.1) 100%)',
        border: '1px solid rgba(25,118,210,0.4)',
      }}
    >
      <Stack spacing={2}>
        {/* Exchange Logo */}
        <Avatar
          src={getExchangeLogo(3)}
          sx={{ width: compact ? 32 : 40, height: compact ? 32 : 40 }}
        />

        {/* Loading State */}
        {loading && !hasData && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: compact ? 2 : 4 }}>
            <CircularProgress size={compact ? 24 : 32} />
          </Box>
        )}

        {/* Error State */}
        {error && !hasData && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: compact ? 2 : 4 }}>
            <Tooltip title="Binance Futures does not support this symbol">
              <BlockIcon sx={{ fontSize: compact ? 36 : 48, color: 'text.disabled' }} />
            </Tooltip>
          </Box>
        )}

        {/* Orderbook Data */}
        {hasData && (
          <>
            {/* Average Price (based on budget) */}
            <Box>
              <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                {t('traderDashboard.standbyHedged.futures.avgSellPrice')}
              </Typography>
              <Typography variant={compact ? 'h5' : 'h4'} fontWeight="bold">
                {avgPrice !== null ? formatPrice(avgPrice, 'U') : '\u2014'}
              </Typography>
            </Box>

            {/* Funding Rate with Countdown */}
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                {t('traderDashboard.standbyHedged.futures.fundingRate')}
              </Typography>
              <Stack direction="column" alignItems="center" spacing={1}>
                <Typography
                  variant={compact ? 'h6' : 'h5'}
                  fontWeight="bold"
                  sx={{
                    color: fundingRate !== null
                      ? fundingRate >= 0
                        ? 'success.main'
                        : 'warning.main'
                      : 'text.primary'
                  }}
                >
                  {fundingRate !== null
                    ? `${fundingRate >= 0 ? '+' : ''}${(fundingRate * 100).toFixed(4)}%`
                    : '\u2014'}
                </Typography>
                {nextFundingTime && (
                  <FundingCountdown nextFundingTime={nextFundingTime} />
                )}
              </Stack>
            </Box>

            {/* Bid Levels - Show highlighted levels (min 5) */}
            <Box>
              <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                {t('traderDashboard.standbyHedged.futures.bidLevels', { count: highlightCount })}
              </Typography>
              <Stack spacing={0.5} sx={{ mt: 1, maxHeight: compact ? 200 : 300, overflow: 'auto' }}>
                {sortedBids.slice(0, Math.max(5, highlightCount)).map((level, idx) => {
                  const isHighlighted = idx < highlightCount;
                  return (
                    <Stack
                      key={idx}
                      direction="row"
                      justifyContent="space-between"
                      sx={{
                        px: 1,
                        py: 0.5,
                        borderRadius: 0.5,
                        background: isHighlighted ? 'rgba(0,255,0,0.15)' : 'transparent',
                        border: isHighlighted ? '1px solid rgba(0,255,0,0.3)' : '1px solid transparent',
                      }}
                    >
                      <Typography variant="body2" fontWeight={isHighlighted ? 600 : 400}>
                        {formatPrice(level.price, 'U')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {level.volume.toFixed(4)}
                      </Typography>
                    </Stack>
                  );
                })}
              </Stack>
            </Box>
          </>
        )}
      </Stack>
    </Paper>
  );
};

/**
 * Spot Card - Shows differential %
 * Highlights ask levels covered by budget
 */
export const SpotOrderbookCard: React.FC<SpotOrderbookCardProps> = ({
  exchangeName,
  exchangeIndex,
  asks,
  loading,
  error,
  quoteCurrency,
  diffPercent,
  avgPrice,
  highlightCount,
  quoteToUsdtRate,
  compact = false,
}) => {
  const { t } = useTranslation();
  const hasData = asks.length > 0;
  const isNonUsdt = quoteCurrency !== 'U';

  // Sort asks ascending (lowest first - best price to buy)
  const sortedAsks = [...asks].sort((a, b) => a.price - b.price);

  // Helper to convert price to USDT
  const toUsdt = (price: number): number | null => {
    if (!quoteToUsdtRate) return null;
    if (quoteCurrency === 'K') {
      // KRW to USDT: divide by rate
      return price / quoteToUsdtRate;
    }
    if (quoteCurrency === 'B') {
      // BTC to USDT: multiply by rate
      return price * quoteToUsdtRate;
    }
    return price;
  };

  // Format USDT equivalent with dynamic decimals based on price magnitude
  // Matches PositionOpportunitiesTable formatPrice logic
  const formatUsdtEquiv = (usdtPrice: number): string => {
    let decimals: number;
    if (usdtPrice >= 1000) {
      decimals = 2;
    } else if (usdtPrice >= 1) {
      decimals = 4;
    } else if (usdtPrice >= 0.0001) {
      decimals = 6;
    } else {
      decimals = 8;
    }
    return `$${usdtPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: decimals })}`;
  };

  // Format average price with more precision for KRW (shows 2 decimals for averages)
  const formatAvgPrice = (price: number): string => {
    if (quoteCurrency === 'K') {
      // KRW: Always show 2 decimal places for average prices
      return `\u20A9${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    } else if (quoteCurrency === 'B') {
      // BTC: up to 8 decimals
      return `\u20BF${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 8 })}`;
    } else {
      // USDT: use standard formatPrice
      return formatPrice(price, quoteCurrency);
    }
  };

  const diffColor = diffPercent !== null
    ? diffPercent < 0
      ? 'success.main'
      : diffPercent > 0
      ? 'error.main'
      : 'text.secondary'
    : 'text.secondary';

  return (
    <Paper
      elevation={3}
      sx={{
        p: compact ? 2 : 3,
        borderRadius: 2,
        background: 'linear-gradient(135deg, rgba(15,18,28,0.95) 0%, rgba(9,12,20,0.98) 100%)',
        border: '1px solid rgba(255,255,255,0.08)',
      }}
    >
      <Stack spacing={2}>
        {/* Exchange Logo */}
        <Avatar
          src={getExchangeLogo(exchangeIndex)}
          sx={{ width: compact ? 32 : 40, height: compact ? 32 : 40 }}
        />

        {/* Loading State */}
        {loading && !hasData && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: compact ? 2 : 4 }}>
            <CircularProgress size={compact ? 24 : 32} />
          </Box>
        )}

        {/* Error State */}
        {error && !hasData && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: compact ? 2 : 4 }}>
            <Tooltip title={`${exchangeName} does not support this symbol`}>
              <BlockIcon sx={{ fontSize: compact ? 36 : 48, color: 'text.disabled' }} />
            </Tooltip>
          </Box>
        )}

        {/* Orderbook Data */}
        {hasData && (
          <>
            {/* Average Price (based on budget) */}
            <Box>
              <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                {t('traderDashboard.standbyHedged.spot.avgBuyPrice')}
              </Typography>
              <Stack direction="row" alignItems="baseline" spacing={1} flexWrap="wrap">
                <Typography variant={compact ? 'h5' : 'h4'} fontWeight="bold">
                  {avgPrice !== null ? formatAvgPrice(avgPrice) : '\u2014'}
                </Typography>
                {/* Show USDT equivalent inline for non-USDT quotes */}
                {isNonUsdt && avgPrice !== null && toUsdt(avgPrice) !== null && (
                  <Typography variant="body1" color="text.secondary">
                    ({formatUsdtEquiv(toUsdt(avgPrice)!)})
                  </Typography>
                )}
              </Stack>
            </Box>

            {/* Differential % (Futures vs Spot, based on avg prices) */}
            {diffPercent !== null && (
              <Box>
                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                  {t('traderDashboard.standbyHedged.spot.differential')}
                </Typography>
                <Typography
                  variant={compact ? 'h6' : 'h5'}
                  fontWeight="bold"
                  sx={{ color: diffColor }}
                >
                  {diffPercent >= 0 ? '+' : ''}{diffPercent.toFixed(2)}%
                </Typography>
              </Box>
            )}

            {/* Ask Levels - Show highlighted levels (min 5, displayed in reverse order) */}
            <Box>
              <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                {t('traderDashboard.standbyHedged.spot.askLevels', { count: highlightCount })}
              </Typography>
              <Stack spacing={0.5} sx={{ mt: 1, maxHeight: compact ? 200 : 300, overflow: 'auto' }}>
                {sortedAsks.slice(0, Math.max(5, highlightCount)).reverse().map((level, idx, arr) => {
                  const sortedIdx = arr.length - 1 - idx;
                  const isHighlighted = sortedIdx < highlightCount;
                  const usdtEquiv = isNonUsdt ? toUsdt(level.price) : null;
                  return (
                    <Stack
                      key={idx}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{
                        px: 1,
                        py: 0.5,
                        borderRadius: 0.5,
                        background: isHighlighted ? 'rgba(255,0,0,0.15)' : 'transparent',
                        border: isHighlighted ? '1px solid rgba(255,0,0,0.3)' : '1px solid transparent',
                      }}
                    >
                      <Stack direction="row" alignItems="baseline" spacing={0.5}>
                        <Typography variant="body2" fontWeight={isHighlighted ? 600 : 400}>
                          {formatPrice(level.price, quoteCurrency)}
                        </Typography>
                        {usdtEquiv !== null && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            ({formatUsdtEquiv(usdtEquiv)})
                          </Typography>
                        )}
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {level.volume.toFixed(4)}
                      </Typography>
                    </Stack>
                  );
                })}
              </Stack>
            </Box>
          </>
        )}
      </Stack>
    </Paper>
  );
};
